import { ReactionStore } from "csi/utility/mobxUtility";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import {
	Exploration,
	ExplorationApi,
	ExplorationApiCategory,
	ExplorationPinnedImage,
	ExplorationCategory,
	FashionInspirationProperties,
	NewExploration,
	CustomActionData,
	ControllerInterfaceWithProductSupport,
} from "./types";
import {
	createNewExploration,
	deleteExploration,
	fetchExplorationPinnedImagesData,
	fetchExplorationsData,
	fetchExplorationCategories,
	fetchFashionInspirationProperties,
	updateExploration,
	updateExplorationImage,
} from "./api";
import { rootStore, routerStore, sessionStore } from "csi/state/rootStore";
import axios, { AxiosInstance } from "axios";
import { passContextToIFrame } from "./utils";
import {
	DEFAULT_TAB_ID_FOR_NEW_PRODUCT,
	DEFAULT_TAB_ID_FOR_NEW_STYLE,
	FashionInspirationIframeEventTypes,
	IFRAME_ID,
	PRODUCT_NODE_TYPE,
	STYLE_NODE_TYPE,
	SUPPORTED_TABS_FOR_NEW_PRODUCT,
	SUPPORTED_TABS_FOR_NEW_STYLE,
	SUPPORTED_TYPES_FOR_NEW_PRODUCT,
	SUPPORTED_TYPES_FOR_NEW_STYLE,
	TYPE_WITH_CUSTOM_FORM_FOR_NEW_STYLE
} from "./constants";
import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import ArrayDataStore from "csi/datastore/ArrayDataStore";
import Writer from "csi/xml/Writer";
import xml from "csi/xml";
import { submitWriter } from "csi/service/writerService";
import csiAction from "csi/action";
import New from "csi/action/New";
import ViewRef from "csi/def/ViewRef";
import Request from "csi/def/Request";
import { TYPE_IMAGE } from "csi/model/image";
import sessionConstants from "csi/sessionConstants";
import { cnl } from "src/c8-platform/type/value";
import { getImageFile } from "csi/utility/fileUtility";
import { createFormData, postFormData, REQUEST_MODULE, REQUEST_OPERATION } from "src/c8-platform/core/request";
import JsonResponse, { JsonResponseData } from "csi/comm/JsonResponse";
import { setLocationTab } from "csi/router";
import { ViewWidget } from "csi/view/Widget";
import { ComponentItem } from "designSystem/types/component-item-types";
import uniqBy from "lodash/uniqBy";
import crews from "csi/access/crews";
import { C8NodeType } from "../c8-platform/type/node";

export class ManageFashionInspirationStore extends ReactionStore {
	/** determines if everything is ready to show Fashion Inspiration IFrame */
	@observable showFashionInspirationIFrame = false;

	@observable url: string;

	/** Fashion Inspiration API base url */
	@observable baseURL: string;

	/** Fashion Inspiration token */
	@observable token: string;

	/** Fashion Inspiration token duration */
	@observable duration: number;

	/** Fashion Inspiration available categories for selection */
	@observable.ref categories: ExplorationCategory[] = [];

	/** Fashion Inspiration available explorations */
	@observable.ref explorationsStore: ArrayDataStore<Exploration>;

	/** Fashion Inspiration available exploration pinned images */
	@observable.ref explorationPinnedImagesStore: ArrayDataStore<ExplorationPinnedImage>;

	/** determines if some data is loading right now */
	@observable isLoading: boolean = true;

	/** determines if New Exploration data is loading right now */
	@observable isLoadingNewExploration: boolean = false;

	/** Fashion Inspiration Exploration id */
	@observable explorationId?: string;

	/** Fashion Inspiration selected group */
	@observable selectedGroup: ComponentItem<string>;

	/** Fashion Inspiration selected category */
	@observable selectedCategory?: ExplorationCategory;

	/** Fashion Inspiration Exploration name */
	@observable explorationName: string = "";

	/** determines if Create New Exploration dialog should be open */
	@observable openNewExplorationDialog: boolean = false;

	/** determines if Fashion Inspiration Delete Exploration dialog should be open */
	@observable openDeleteExplorationDialog: boolean = false;

	/** determines if Fashion Inspiration Rename Exploration dialog should be open */
	@observable openRenameExplorationDialog: boolean = false;

	/** determines if Exploration Pinned Images pane should be open */
	@observable showExplorationPinnedImagesPane: boolean = false;

	/** determines if Fashion Inspiration Iframe dialog should be open */
	@observable openIFrameDialog: boolean = false;

	/** selected Pinned Image url */
	@observable selectedPinnedImageUrl: cnl | null;

	/** keys are tabs name, values are action data for them to resolve new style action with no errors */
	private tabsNameToActionMap: Record<string, CustomActionData> = {
		AllStyles: {
			m_id: "NewStyleWizard",
			m_typeNode: STYLE_NODE_TYPE,
			classController: "Apparel/NewStyleWizard",
			m_viewRef: Object.assign(new ViewRef(), {
				m_id: "NewStyleWizard",
				m_url: "centric://APPAREL/Views",
			}),
			m_request: this.styleRequest,
		},
		__Print__: {
			m_id: "NewStyleWizard",
			m_typeNode: STYLE_NODE_TYPE,
			classController: "Apparel/NewStyleWizardInGallery",
			m_viewRef: Object.assign(new ViewRef(), {
				m_id: "NewStyleWizard",
				m_url: "centric://APPAREL/Views",
			}),
			m_request: this.styleRequest,
		},
		"Collection/AllStyles": {
			m_id: "NewStyleForm",
			m_typeNode: STYLE_NODE_TYPE,
			classController: "Apparel/NewStyle",
			m_viewRef: Object.assign(new ViewRef(), {
				m_id: "__Form__",
			}),
			m_request: this.styleRequest,
		},
		"Collection/__Print__": {
			m_id: "NewStyleForm",
			m_typeNode: STYLE_NODE_TYPE,
			classController: "Apparel/NewStyleInGallery",
			m_viewRef: Object.assign(new ViewRef(), {
				m_id: "__Form__",
			}),
			m_request: this.styleRequest,
		},
		/** Merchandising Target tab */
		Target: {
			m_id: "NewProductVersion",
			m_typeNode: "MerchProduct",
			classController: "Merchandising/NewProductVersionController",
			classControllerParams: {
				bShowWait: true,
			},
			m_viewRef: Object.assign(new ViewRef(), {
				m_id: "NewProductVersionWizard",
			}),
			m_request: null,
		},
	};

	@computed get fashionInspirationApi(): AxiosInstance {
		const { baseURL, token, updatePropertiesState } = this;
		const headers = {};
		if (token) {
			headers["Authorization"] = token;
		}
		const api = axios.create({
			baseURL,
			headers,
		});

		// Add a request interceptor to add token to each request
		api.interceptors.request.use(
			(config) => {
				if (token) {
					config.headers.Authorization = token;
				}
				return config;
			},
			(error) => Promise.reject(error),
		);

		// Add a response interceptor that will refresh token if 401 error is received
		api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;

				// If the error status is 401 and there is no originalRequest._retry flag,
				// it means the token has expired and it must be refreshed
				if (error.response?.status === 401 && !originalRequest._retry) {
					originalRequest._retry = true;

					try {
						const properties = await fetchFashionInspirationProperties();

						updatePropertiesState(properties);

						// Retry the original request with the new token
						originalRequest.headers.Authorization = encodeURIComponent(properties.token);

						return axios(originalRequest);
					} catch (error) {
						this.setError(error);
					}
				}

				return Promise.reject(error);
			},
		);

		return api;
	}

	get iframeUrl(): string | null {
		return this.baseURL && this.explorationId ? `${this.baseURL}/exploration/${this.explorationId}/` : null;
	}
	
	get styleRequest(): Request {
		return Object.assign(new Request(), {
			m_module: "Apparel",
			m_operation: "NewStyleFromTemplateOrStyleType",
		});
	}

	/** get the list of unique groups from categories */
	get groups(): ComponentItem<string>[] | null {
		return uniqBy(this.categories, "group").map((category) => ({ id: category.group }));
	}

	get categoriesPerGroup(): ExplorationCategory[] | null {
		return this.categories.filter((category) => category.group === this.selectedGroup?.id);
	}

	get isOnlyOneCategoryInGroup(): boolean {
		return this.categoriesPerGroup.length === 1;
	}

	get hasPinnedImages(): boolean {
		return  Boolean(this.explorationPinnedImagesStore && this.explorationPinnedImagesStore.total > 0);
	}

	get showLoader(): boolean {
		return  Boolean(this.isLoading && !this.openNewExplorationDialog && !this.openIFrameDialog);
	}

	get showNewExplorationPane(): boolean {
    	return  Boolean(this.explorationsStore && this.explorationsStore.total === 0 && !this.isLoading);
	}
	
	get showExplorationsListPane(): boolean {
    	return  Boolean(this.explorationsStore && this.explorationsStore.total > 0 && !this.isLoading && !this.showExplorationPinnedImagesPane);
	}

	get showIframe(): boolean {
    	return Boolean(this.token && routerStore.locationName && sessionStore.locale && this.iframeUrl);
	}

	get selectedExplorationData(): Exploration | undefined {
		const index = this.explorationsStore?.getIdentityIndex(this.explorationId);

    	return this.explorationsStore?.get(index);
	}

	@action updatePropertiesState = (properties: FashionInspirationProperties): void => {
		this.token = encodeURIComponent(properties.token);
		this.duration = properties.duration;
		this.baseURL = properties.url;
	};

	@action setOpenFashionInspirationIFrame = (): void => {
		this.showFashionInspirationIFrame = true;
	};

	@action setOpenExplorationPinnedImagesPane = (): void => {
		this.fetchExplorationPinnedImages();
		this.showExplorationPinnedImagesPane = true;
	};
	
	@action setCloseExplorationPinnedImagesPane = (): void => {
		this.setExplorationId(null);
		this.selectedPinnedImageUrl = null;
		this.showExplorationPinnedImagesPane = false;
	};

	@action setOpenNewExplorationDialog = (): void => {
		this.openNewExplorationDialog = true;
	};

	@action setCloseNewExplorationDialog = (): void => {
		this.resetData();
		this.openNewExplorationDialog = false;
	};

	@action setOpenIframeDialog = (): void => {
		this.openIFrameDialog = true;
	};

	@action setCloseIframeDialog = (): void => {
		this.fetchInitialData();
		this.openIFrameDialog = false;
		this.setOpenExplorationPinnedImagesPane();
	};

	@action setSelectedCategory = (value: ExplorationCategory | null): void => {
		this.selectedCategory = value;
	};

	@action setSelectedGroup = (value: ComponentItem<string>): void => {
		this.selectedGroup = value;
	};

	@action setExplorationName = (value: string): void => {
		this.explorationName = value;
	};

	@action setExplorationId = (value: string | null): void => {
		this.explorationId = value;
	};

	@action setSelectedPinnedImageUrl = (url: string | null): void => {
		this.selectedPinnedImageUrl = url;
	};

	@action resetData = (): void => {
		this.setSelectedGroup(null);
		this.setSelectedCategory(null);
		this.setExplorationName("");
	};

	@action fetchInitialData = async (): Promise<void> => {
		this.isLoading = true;
		await this.fetchCategories();
		await this.fetchExplorations();
		runInAction(() => {
			this.isLoading = false;
		});
	};

	@action fetchCategories = async (): Promise<void> => {
		this.resetData();

		try {
			const explorationCategories = <ExplorationApiCategory[]> await fetchExplorationCategories({
				api: this.fashionInspirationApi,
				locale: sessionStore.locale,
			});

			runInAction(() => {
				this.categories = explorationCategories.map(({ category_id, category_name, category_group, icon }) => ({
					id: category_id,
					name: category_name,
					icon: icon,
					group: category_group,
				}));
			});
		} catch (error) {
			this.setError(error);
		}
	};

	@action createNewExploration = async (defaultExplorationName: string): Promise<void> => {
		if (!this.selectedCategory?.id) {
			return;
		}

		try {
			this.isLoadingNewExploration = true;
			const newExplorationData: NewExploration = {
				name: this.explorationName || defaultExplorationName,
				category: this.selectedCategory.id,
				tenant_reference_id: routerStore.locationUrl,
			};
			const newExploration = await createNewExploration({
				api: this.fashionInspirationApi,
				data: newExplorationData,
			});

			runInAction(() => {
				this.setExplorationId(newExploration.id);
			});
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.isLoadingNewExploration = false;
			});
		}
	};

	@action fetchExplorations = async (): Promise<void> => {
		try {
			const explorationsData = await fetchExplorationsData({
				api: this.fashionInspirationApi,
				tenantReferenceId: routerStore.locationUrl,
			});
			runInAction(() => {
				this.setExplorationsStore(explorationsData);
			});
		} catch (error) {
			this.setError(error);
		}
	};

	@action fetchExplorationPinnedImages = async (): Promise<void> => {
		if (!this.explorationId) {
			return;
		}
		try {
			this.isLoading = true;
			const data = await fetchExplorationPinnedImagesData({
				id: this.explorationId,
				api: this.fashionInspirationApi,
			});
			runInAction(() => {
				this.explorationPinnedImagesStore = new ArrayDataStore<ExplorationPinnedImage>(data, "id");
			});
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	@action setExplorationsStore = (explorationData: ExplorationApi[]): void => {
		const explorations = explorationData.map(({
			id,
			category,
			name,
			thumbnail,
			created_at,
			updated_at,
		}) => ({
			id,
			category,
			name,
			thumbnail,
			thumbnailIcon: this.categories.find((categoryItem) => categoryItem.id === category)?.icon,
			createdAt: created_at,
			updatedAt: updated_at,
		}));

		this.explorationsStore = new ArrayDataStore<Exploration>(explorations, "id");
	};

	@action setOpenDeleteExplorationDialog = (open: boolean): void => {
		this.openDeleteExplorationDialog = open;
	};

	/** action that is called when Delete item is clicked from Exploration three-dots menu */
	@action handleDeleteExplorationMenuItemClick = (explorationId: string, explorationName: string): void => {
		this.setExplorationId(explorationId);
		this.setExplorationName(explorationName);
		this.setOpenDeleteExplorationDialog(true);
	};

	/** action that handles Exploration Delete of this.explorationId */
	@action handleDeleteExploration = async (): Promise<void> => {
		if (!this.explorationId) {
			return;
		}
		try {
			this.setOpenDeleteExplorationDialog(false);
			this.isLoading = true;
			await deleteExploration({
				id: this.explorationId,
				api: this.fashionInspirationApi,
			});
			await this.fetchExplorations();
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.resetData();
				this.isLoading = false;
			});
		}
	};


	@action handleRenameExplorationMenuItemClick = (explorationId: string, explorationName: string): void => {
		this.setExplorationId(explorationId);
		this.setExplorationName(explorationName);
		this.setOpenRenameExplorationDialog(true);
	};

	@action setOpenRenameExplorationDialog = (open: boolean): void => {
		this.openRenameExplorationDialog = open;
	};

	@action handleRenameExploration = async (): Promise<void> => {
		if (!this.explorationId || !this.explorationName) {
			return;
		}

		try {
			this.setOpenRenameExplorationDialog(false);
			this.isLoading = true;
			const updateExplorationData: Pick<NewExploration, "name"> = {
				name: this.explorationName,
			};
			await updateExploration({
				id: this.explorationId,
				api: this.fashionInspirationApi,
				data: updateExplorationData,
			});
			await this.fetchExplorations();
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.resetData();
				this.isLoading = false;
			});
		}
	};

	@action handleUnpinExplorationImage = async (imageId: string): Promise<void> => {
		try {
			this.isLoading = true;
			const updateExplorationImageData: Pick<ExplorationPinnedImage, "is_pinned"> = {
				is_pinned: false,
			};
			const updatedImageData = await updateExplorationImage({
				id: imageId,
				api: this.fashionInspirationApi,
				data: updateExplorationImageData,
			});
			if (updatedImageData.id === imageId && !updatedImageData.is_pinned) {
				this.explorationPinnedImagesStore.remove(imageId);
			}
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	@action fetchProperties = async (): Promise<void> => {
		try {
			this.isLoading = true;
			const properties = await fetchFashionInspirationProperties();
			this.updatePropertiesState(properties);
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	handleIFrameMessage = (event: MessageEvent<any>): void => {
		// we receive a message when the inner iframe has been fully loaded and our context_component
		// is ready to receive data from us
		if (event.data.type && event.data.type === FashionInspirationIframeEventTypes.READY) {
			passContextToIFrame(
				IFRAME_ID,
				{
					token: this.token,
					nodeName: routerStore.locationName,
					locale: sessionStore.locale,
				},
			);
		} else if (event.data.type && event.data.type === FashionInspirationIframeEventTypes.REFRESH_TOKEN) {
			this.fetchProperties();
		}
	};

	@action onChangeExplorationName = (e: ChangeEvent<HTMLInputElement>): void => this.setExplorationName(e.target.value);

	@action onChangeCategory = (event: ChangeEvent, newCategory: ExplorationCategory, reason: AutocompleteChangeReason): void => this.setSelectedCategory(newCategory);

	@action onChangeGroup = (event: ChangeEvent, newGroup: ComponentItem<string>, reason: AutocompleteChangeReason): void => {
		this.setSelectedGroup(newGroup);
		// set the defaultValue for category, first item from categories list
		this.setSelectedCategory(this.categoriesPerGroup[0]);
	};

	@action handleOpenExploration = (id: string): void => {
		this.setExplorationId(id);
		this.setOpenIframeDialog();
	};

	@action handleOpenExplorationPinnedImagesPane = (id: string):void => {
		this.setExplorationId(id);
		this.setOpenExplorationPinnedImagesPane();
	};

	get isSupportedMerchandisingLocationType(): boolean {
		return SUPPORTED_TYPES_FOR_NEW_PRODUCT.includes(routerStore.locationType);
	}

	get isSupportedStyleLocationType(): boolean {
		return SUPPORTED_TYPES_FOR_NEW_STYLE.includes(routerStore.locationType);
	}

	/** get New Button localized type */
	get newButtonLocType(): C8NodeType {
		if (this.isSupportedMerchandisingLocationType) {
			return PRODUCT_NODE_TYPE;
		}
		if (this.isSupportedStyleLocationType) {
			return STYLE_NODE_TYPE;
		}
	}

	/** Show New Style or New Merch Product button only on supported hierarchy levels
	 * or hide it if user doesn't have a proper security role
	 */
	get isNewButtonAvailable(): boolean {
		return (this.isSupportedStyleLocationType && this.canCreateNewStyle) ||
			(this.isSupportedMerchandisingLocationType && this.canCreateNewMerchProduct);
	};

	get isOpenExplorationButtonAvailable(): boolean {
		return this.hasPinnedImages;
	};

	get currentView():  ViewWidget | undefined  {
		return rootStore.layoutStore?.selectedItem?.view;
	}

	/** Check that user has proper roles to create new Style from FI right panel */
	get canCreateNewStyle():  boolean  {
		return crews.isInRoles(["Style Administrator"], routerStore.locationNode);
	}
	
	/** Check that user has proper roles to create new Merch Product from FI right panel */
	get canCreateNewMerchProduct():  boolean  {
		return crews.isInRoles(["Business Plan Administrator"], routerStore.locationNode);
	}

	/** if current location tab doesn't support New Action then redirect to DEFAULT_TAB_ID_FOR_NEW_STYLE */
	redirectToTabWithNewStyleActionSupport = async (): Promise<void> => {
		const currentTabId = this.currentView?.layoutItem?.id;
		if (this.isSupportedStyleLocationType && !SUPPORTED_TABS_FOR_NEW_STYLE.includes(currentTabId)) {
			await setLocationTab(DEFAULT_TAB_ID_FOR_NEW_STYLE);
		}
		if (this.isSupportedMerchandisingLocationType && !SUPPORTED_TABS_FOR_NEW_PRODUCT.includes(currentTabId)) {
			await setLocationTab(DEFAULT_TAB_ID_FOR_NEW_PRODUCT);
		}
	};

	getTabNameForCollection(tab: string): string {
		return tab === DEFAULT_TAB_ID_FOR_NEW_STYLE ? "Collection/__Print__" : "Collection/AllStyles";
	};

	get actionDataForCurrentTab(): CustomActionData {
		if (routerStore.locationType === TYPE_WITH_CUSTOM_FORM_FOR_NEW_STYLE) {
			return this.tabsNameToActionMap[this.getTabNameForCollection(routerStore.targetTab)];
		}

		return this.tabsNameToActionMap[routerStore.targetTab];
	};

	/** Action that opens New wizard popup */
	handleOpenNewWizard = async (): Promise<void> => {
		await this.redirectToTabWithNewStyleActionSupport();
		const action = Object.assign(new New(), {
			...this.actionDataForCurrentTab,
			classControllerParams: {
				...this.actionDataForCurrentTab.classControllerParams,
				imageUrl: this.selectedPinnedImageUrl,
				onPublishResponseAsync: this.postActionUploadImage,
			},
		});
		action.resolve();
		csiAction.invoke(this.currentView, null, action);
	};

	getActionNodeCnls = (actionController: ControllerInterfaceWithProductSupport): cnl[] => {
		if (this.isSupportedStyleLocationType) {
			return [actionController.m_urlNode];
		}
		if (this.isSupportedMerchandisingLocationType) {
			return actionController._productUrls || [];
		}
	};

	addImageToNodeCnls = async (imageUrl: string, cnls: cnl[]): Promise<JsonResponse> => {
		const writer = new Writer();
		cnls.forEach((newCnl) => {
			writer.changeNode(newCnl).changeAttributeKeys("Images", xml.ATTR_REFMAP, {
				"": imageUrl,
				1: imageUrl,
			});
		});
		return await submitWriter(writer);
	};

	/** upload selectedPinnedImage to user image storage */
	uploadSelectedPinnedImage = async (): Promise<string> => {
		const imageFile = await getImageFile(this.selectedPinnedImageUrl);
		const formData = createFormData({
			[REQUEST_MODULE]: "SiteAdmin",
			[REQUEST_OPERATION]: "MultiFilePublish",
			Prepend: true,
			NewNodeTypes: TYPE_IMAGE,
			URL: sessionStore.userPreferencesUrl,
			SessionURL: sessionConstants.sessionUrl,
			FormFileFieldName: "imageFiles",
			imageFiles: [imageFile],
		});
		const res = await postFormData<JsonResponseData>(formData);
		const jsonResponse = new JsonResponse(res);

		return jsonResponse.getResultNode().$URL;
	};

	/** upload image to the new node cnls as soon as action is finished */
	@action postActionUploadImage = async (actionController: ControllerInterfaceWithProductSupport): Promise<void> => {
		if (!this.selectedPinnedImageUrl) {
			this.setError(new Error("No image URL selected for upload."));
			return;
		}
		try {
			this.isLoading = true;
			/** it's important to get cnls before image upload because after actionController object will be changed */
			const newNodeCnls =  this.getActionNodeCnls(actionController);
			const imageUrl = await this.uploadSelectedPinnedImage();
			const response = await this.addImageToNodeCnls(imageUrl, newNodeCnls);
			actionController.m_cache.cacheResponse(response);
			if (this.isSupportedMerchandisingLocationType) {
				await rootStore?.layoutStore?.selectedItem?.view?.refreshData();
			}
		} catch (error) {
			this.setError(error);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	constructor(properties: FashionInspirationProperties) {
		super();
		this.updatePropertiesState(properties);
		makeObservable(this);
	}
}

export const ManageFashionInspirationStoreContext = createContext<ManageFashionInspirationStore>(undefined);
export const useManageFashionInspirationStore = (): ManageFashionInspirationStore => useContext(ManageFashionInspirationStoreContext);
